/**
 * Banner 进度条：替换小圆点
 * - 图片：按 data-duration（秒）填充，结束后切下一张
 * - 视频：按播放进度填充，播放结束后切下一张
 * - 每次页面加载 / bfcache 恢复：强制回到第 1 张并重新计时播放
 */
class SlideshowProgress extends HTMLElement {
  connectedCallback() {
    this.segments = Array.from(this.querySelectorAll('[data-slideshow-seg]'));
    this.durationMs = Math.max(1, parseFloat(this.dataset.duration || '5')) * 1000;
    this.enabled = this.dataset.enabled === 'true';
    this._raf = null;
    this._imageStart = 0;
    this._imagePausedAt = 0;
    this._cleanupVideo = null;
    this._paused = false;
    this._boundChange = this.onSlideChange.bind(this);
    this._boundPause = this.pause.bind(this);
    this._boundPlay = this.resume.bind(this);
    this._boundPageShow = this.onPageShow.bind(this);

    this.segments.forEach((seg) => {
      seg.addEventListener('click', (event) => {
        event.preventDefault();
        const index = parseInt(seg.dataset.index, 10);
        if (Number.isNaN(index) || !this.slider) return;
        if (typeof this.slider.select === 'function') {
          this.slider.select(index);
        } else if (this.slider.slider) {
          this.slider.slider.select(index);
        }
      });
    });

    this.whenSliderReady().then((slider) => {
      this.slider = slider;
      // 关闭 Flickity 内置自动播放，改由本进度条驱动
      if (this.slider.slider) {
        this.slider.slider.options.autoPlay = false;
        this.slider.slider.pausePlayer?.();
        this.slider.slider.stopPlayer?.();
      }
      this.slider.addEventListener('slider:change', this._boundChange);
      this.slider.addEventListener('slider:pause', this._boundPause);
      this.slider.addEventListener('slider:play', this._boundPlay);
      window.addEventListener('pageshow', this._boundPageShow);

      /* 首屏：回到第 1 张并重新计时（覆盖刷新残留 / 浏览器媒体记忆） */
      this.resetToFirstAndRestart();
    });
  }

  disconnectedCallback() {
    this.stop();
    window.removeEventListener('pageshow', this._boundPageShow);
    if (this.slider) {
      this.slider.removeEventListener('slider:change', this._boundChange);
      this.slider.removeEventListener('slider:pause', this._boundPause);
      this.slider.removeEventListener('slider:play', this._boundPlay);
    }
  }

  /**
   * bfcache 返回页面时同样重置到第一张并重新计时
   */
  onPageShow(event) {
    if (!this.slider) return;
    /* persisted=true 表示从往返缓存恢复，状态可能停在中间某张 */
    if (event.persisted || this.currentIndex !== 0) {
      this.resetToFirstAndRestart();
    }
  }

  /**
   * 强制选中第 0 张、视频 currentTime 归零、进度条重头计时
   */
  resetToFirstAndRestart() {
    if (!this.slider) return;

    this.stop();
    this._paused = false;
    this.resetAllVideosToStart();

    const flickity = this.slider.slider;
    if (flickity && typeof flickity.select === 'function') {
      /* 立即切到第 1 张，不带动画，避免刷新后还停在上次位置 */
      flickity.select(0, false, true);
    } else if (typeof this.slider.select === 'function') {
      this.slider.select(0);
    } else {
      this.slider.setAttribute?.('selected-index', '0');
    }

    /* select 会触发 change；若已在 0 则可能不触发，这里主动重开计时 */
    this.onSlideChange();
  }

  /** 所有 slide 内视频回到开头，避免刷新后从半截继续播 */
  resetAllVideosToStart() {
    const items = this.slider?.items || [];
    items.forEach((slide) => {
      slide.querySelectorAll('video').forEach((video) => {
        try {
          video.pause();
          video.currentTime = 0;
        } catch (err) {}
      });
    });
  }

  /** 等待 slideshow-element 完成 Flickity 初始化 */
  whenSliderReady() {
    const id = this.getAttribute('aria-controls');
    const el = id ? document.getElementById(id) : null;
    if (!el) return Promise.resolve(null);

    if (el.slider || el.items?.length <= 1) {
      return Promise.resolve(el);
    }

    return new Promise((resolve) => {
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        if (el.slider || el.items?.length <= 1 || tries > 80) {
          clearInterval(timer);
          resolve(el);
        }
      }, 50);
    });
  }

  get currentIndex() {
    if (!this.slider) return 0;
    return this.slider.selectedIndex ?? this.slider.slider?.selectedIndex ?? 0;
  }

  get currentSlide() {
    return this.slider?.items?.[this.currentIndex] || null;
  }

  onSlideChange() {
    this.stop();
    this._paused = false;
    const index = this.currentIndex;

    this.segments.forEach((seg, i) => {
      const fill = seg.querySelector('[data-slideshow-fill]');
      const isActive = i === index;
      const isComplete = i < index;
      seg.setAttribute('aria-current', isActive ? 'true' : 'false');
      seg.classList.toggle('is-complete', isComplete);
      seg.classList.toggle('is-active', isActive);
      if (fill) {
        fill.style.transition = 'none';
        // 已播完的段恢复为轨道底色（半透明），不再保持实心白填充
        fill.style.width = '0%';
      }
    });

    if (!this.enabled || !this.slider || this.segments.length < 2) return;

    const slide = this.currentSlide;
    if (!slide) return;

    if (slide.getAttribute('data-type') === 'video') {
      this.startVideoProgress(slide);
    } else {
      this.startImageProgress();
    }
  }

  /** 图片：线性填充至配置时长后切下一张 */
  startImageProgress() {
    const seg = this.segments[this.currentIndex];
    const fill = seg?.querySelector('[data-slideshow-fill]');
    if (!fill) return;

    this._imageStart = performance.now();
    this._imagePausedAt = 0;

    const tick = (now) => {
      if (this._paused) {
        this._raf = requestAnimationFrame(tick);
        return;
      }
      const elapsed = now - this._imageStart;
      const progress = Math.min(1, elapsed / this.durationMs);
      fill.style.width = `${progress * 100}%`;
      if (progress >= 1) {
        this.goNext();
        return;
      }
      this._raf = requestAnimationFrame(tick);
    };

    this._raf = requestAnimationFrame(tick);
  }

  /** 视频：跟随 currentTime；ended 后切下一张（无原生 video 时回退为时长计时） */
  startVideoProgress(slide) {
    const seg = this.segments[this.currentIndex];
    const fill = seg?.querySelector('[data-slideshow-fill]');
    if (!fill) return;

    let attempts = 0;
    const bind = () => {
      attempts += 1;
      const mediaList = slide.querySelectorAll('video-media');
      const media =
        typeof theme !== 'undefined' && theme.utils?.visibleMedia
          ? theme.utils.visibleMedia(mediaList)
          : mediaList[0];
      const video = media?.querySelector('video');

      if (!video) {
        if (attempts < 40) {
          this._videoRetry = setTimeout(bind, 150);
        } else {
          // YouTube/Vimeo 等：退回图片计时逻辑
          this.startImageProgress();
        }
        return;
      }

      video.loop = false;

      /* 每次进入该 slide：从头播，进度条同步从 0 开始 */
      try {
        video.currentTime = 0;
      } catch (err) {}
      fill.style.width = '0%';

      const onTimeUpdate = () => {
        if (this._paused || !video.duration || Number.isNaN(video.duration)) return;
        fill.style.width = `${Math.min(100, (video.currentTime / video.duration) * 100)}%`;
      };

      const onEnded = () => {
        fill.style.width = '100%';
        this.goNext();
      };

      video.addEventListener('timeupdate', onTimeUpdate);
      video.addEventListener('ended', onEnded);

      this._cleanupVideo = () => {
        video.removeEventListener('timeupdate', onTimeUpdate);
        video.removeEventListener('ended', onEnded);
      };

      // 确保当前可见视频在播放（主题会在切页时 play，这里再补一次）
      if (typeof media.play === 'function') {
        media.play();
      } else if (video.paused) {
        video.play().catch(() => {});
      }
    };

    bind();
  }

  pause() {
    if (this._paused) return;
    this._paused = true;
    this._imagePausedAt = performance.now();

    const slide = this.currentSlide;
    if (slide?.getAttribute('data-type') === 'video') {
      const mediaList = slide.querySelectorAll('video-media');
      const media =
        typeof theme !== 'undefined' && theme.utils?.visibleMedia
          ? theme.utils.visibleMedia(mediaList)
          : mediaList[0];
      media?.pause?.();
    }
  }

  resume() {
    if (!this._paused) return;
    if (this._imagePausedAt) {
      this._imageStart += performance.now() - this._imagePausedAt;
      this._imagePausedAt = 0;
    }
    this._paused = false;

    const slide = this.currentSlide;
    if (slide?.getAttribute('data-type') === 'video') {
      const mediaList = slide.querySelectorAll('video-media');
      const media =
        typeof theme !== 'undefined' && theme.utils?.visibleMedia
          ? theme.utils.visibleMedia(mediaList)
          : mediaList[0];
      media?.play?.();
    }
  }

  stop() {
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = null;
    }
    if (this._videoRetry) {
      clearTimeout(this._videoRetry);
      this._videoRetry = null;
    }
    if (this._cleanupVideo) {
      this._cleanupVideo();
      this._cleanupVideo = null;
    }
  }

  goNext() {
    this.stop();
    if (!this.slider) return;
    if (this.slider.slider) {
      this.slider.slider.next();
    } else {
      this.slider.dispatchEvent(new CustomEvent('slider:next', { bubbles: true }));
    }
  }
}

customElements.define('slideshow-progress', SlideshowProgress);
